import { useMemo, useState, useCallback, useContext, useEffect } from 'react';
import { stableSort, dateComparator, descendingComparator } from '../helpers/MRTable';
import { workOrdersByDay } from '../helpers/WorkOrders';
import { escapeRegExp } from '../helpers/SearchToolbar';
import StoreContext from '../context/StoreContext';
import WorkOrderContext from '../context/WorkOrderContext';

export const useMRTable = (currentRows, searchText, setSearchText, day, type) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openRows, setOpenRows] = useState({});
  const [tableRows, setTableRows] = useState(currentRows);
  const [visibleRows, setVisibleRows] = useState([]);
  const { time_zone, storeId } = useContext(StoreContext)
  const { setWorkOrders } = useContext(WorkOrderContext)

  useEffect(() => {
    if(type === 'WorkOrder') {
      setTableRows(workOrdersByDay(currentRows, time_zone, day))
    } else if(type === 'StoreList') {
      setTableRows(currentRows)
    }
  }, [currentRows, time_zone]);

  useEffect(() => {
    setPage(0)
    setOrder('asc')
    setOrderBy('')
  },[storeId])

  const getComparator = useCallback((order, orderBy) => {
    if (orderBy === 'DateField') {
      return order === 'desc'
        ? (a, b) => dateComparator(a.customer_eta, b.customer_eta, time_zone)
        : (a, b) => -dateComparator(a.customer_eta, b.customer_eta, time_zone);
    }
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }, [time_zone]);

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    setOpenRows({});
    setPage(0); // reset to first page on new search
  };

  const emptySearch = () => {
    setSearchText('')    
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setOpenRows({});
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setOpenRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRow = (rowId) => {
    setOpenRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const filteredRows = useMemo(() => {
    if (!searchText) return tableRows;
    const searchRegex = new RegExp(escapeRegExp(searchText), 'i');
    const woSearchText = Number(searchText).toString();
    const cleanWoSearchRegex = new RegExp(escapeRegExp(woSearchText), 'i');
    return tableRows.filter((row) => {
      if(type === 'WorkOrder') {
        return searchRegex.test(row.Name?.toString() || '') ||
          cleanWoSearchRegex.test(Number(row.WO || 0).toString()) ||
          searchRegex.test(row.Vehicle?.toString() || '') ||
          searchRegex.test(row.Phone?.toString() || '');
      } else if(type === 'StoreList') {
        return searchRegex.test(row.value?.toString() || '') ||
          searchRegex.test(row.label?.toString() || '');
      }
    });
  }, [tableRows, searchText]);

  const sortedRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy));
  }, [filteredRows, order, orderBy]);


  useEffect(() => {
    const start = page * rowsPerPage;
    const sortWOs = sortedRows.slice(start, start + rowsPerPage);
    setVisibleRows(sortWOs)
  }, [sortedRows, page, rowsPerPage]);

  // Add SMS data to the appropriate WO
  const updateSmsData = (switchToUpdate) => {
    setWorkOrders((prev) => {
      const WO = prev.findIndex((wo) => `${wo.WO}` === switchToUpdate.WO);
      if(prev[WO]){
        if(!prev[WO]?.SmsData){
          prev[WO].SmsData = {
            callStoreMessage: [],
            beBackMessage: [],
            vehicleReadyMessage: [],
            promisedTimeMessage: [],
            promisedTimeMessageUpdate: []
          }
        }

        prev[WO].SmsData = {
          ...prev[WO].SmsData, 
          [switchToUpdate.smsType]: [
            {...switchToUpdate.smsData},
            ...prev[WO].SmsData[switchToUpdate.smsType]
          ]
        } 
        
      }
      return [...prev];
    });
  };

  return {
    requestSearch,
    emptySearch,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handleRequestSort,
    page,
    setPage,
    toggleRow,
    openRows,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    visibleRows,
    setVisibleRows,
    rowsCount: filteredRows.length,
    updateSmsData, // exposed for useEffect in component
  };
};
